import fetch from 'isomorphic-fetch';

import {
  updateAnonymousUsername,
  updateShareVideoName,
  updateVideoCta,
  updateVideoDescriptionState,
} from 'actions/shareVideo';
import {
  fetchVideoPlayerVideo,
  requestVideoPlayerCurrentTime,
  requestVideoPlayerPause,
  requestVideoPlayerPlay,
  updateVideoPlayerDimensions,
  updateVideoPlayerDuration,
  updateVideoPlayerEmojiSelected,
  updateVideoPlayerEmojiSelectedReset,
  updateVideoPlayerEmojiSelectedSaved,
  updateVideoPlayerFlippedCamera,
  updateVideoPlayerInAnonymousReactionMode,
  updateVideoPlayerInCommentMode,
  updateVideoPlayerLocalReactions,
  updateVideoPlayerName,
  updateVideoPlayerNeedsPassword,
  updateVideoPlayerNotWaiting,
  updateVideoPlayerOwner,
  updateVideoPlayerPassword,
  updateVideoPlayerPlainSourceUrl,
  updateVideoPlayerPosterUrl,
  updateVideoPlayerReactions,
  updateVideoPlayerRemoveReaction,
  updateVideoPlayerShowComments,
  updateVideoPlayerSource,
  updateVideoPlayerSourceFromClip,
  updateVideoPlayerVideo,
  updateVideoPlayerWaiting,
  updateVideoViewerNeedsPermission,
} from 'actions/videoPlayer';
import EMOJI_REACTIONS_MAP, { EMOJIS } from 'constants/emojiReactions';
import * as analyticsEvents from 'constants/events.js';
import { HEADER_CONTENT_TYPE } from 'constants/http';
import {
  EMBED_DIMENSIONS_UPDATE,
  EMBED_VIDEO_CORRECT_PASS,
  EMBED_VIDEO_DURATION,
} from 'constants/messages';
import { DASH, JSON as JSON_MIME, M3U8, MP4, WEBM } from 'constants/mimes';
import { LOOM_URI, SERVER_ERROR_PAGE } from 'constants/routes';
import { EVT_VIEW } from 'constants/videoAnalyticsEvents';
import {
  PLAYER_LONG_STEP,
  STEP_BACK,
  STEP_FORWARD,
} from 'constants/videoPlayer';
import { removeAllLocalDeleted } from 'creators/comments';
import { selectAnonymousUsername } from 'selectors/shareVideo';
import {
  selectHasEnded,
  selectIsMainVideoOnSharePage,
  selectPlayerTime,
  selectVideo,
} from 'selectors/videoPlayer';
import * as analytics from 'utilities/analytics';
import { getAnonUserId, getAnonUserName } from 'utilities/auth';
import { getDashPlaylistWithCredentials } from 'utilities/dash';
import { getAppSource } from 'utilities/device';
import { getGraphQLClient } from 'utilities/graphql';
import { getHlsPlaylistWithCredentials } from 'utilities/hls';
import * as sentry from 'utilities/sentry';
import {
  convertTrimTimeToFullTime,
  findCurrentPlayRangeIdx,
  findNewTimeInPlayRanges,
  waitingForTrim,
} from 'utilities/trimming';
import { getParam, removeParam } from 'utilities/url';
import * as videoUtils from 'utilities/video';
import videoSession from 'utilities/video-session';

import GetPasswordProtectedProps from './GetPasswordProtectedProps.graphql';
import { updateTrimRangesCreator } from './trimming';

const fromPreviewPanel = getParam('fromPreviewPanel') === 'true';
const sharedAppSource = getParam('sharedAppSource');
const MAX_USER_NAME_LENGTH = 200;

if (sharedAppSource) {
  removeParam(window)('sharedAppSource');
}

export function initVideoCreator(videoModel) {
  return dispatch => dispatch(updateVideoCreator(videoModel));
}

export function sendInactiveVideoOwnerPing() {
  return (dispatch, getState) => {
    const {
      video: { id: videoId },
    } = getState().video_player;

    fetch(`/v1/videos/${videoId}/inactivity_ping`, {
      credentials: 'include',
      method: 'POST',
    })
      .then(resp => {
        if (resp.status >= 400) {
          throw new Error(
            `Oops! Error while pinging owner - HTTP response: ${resp.status}`
          );
        }

        return resp;
      })
      .catch(err => {
        sentry.logException(err, 'error while pinging owner creator');
      });
  };
}

export function trackVideoViewCreator() {
  return (dispatch, getState) => {
    if (videoSession.enabled) {
      videoSession.sendEvent(EVT_VIEW);

      return;
    }

    const {
      analytics: videoAnalytics,
      from_link_expand: fromLinkExpand,
      parent_location: parentLocation,
      password,
      video,
    } = getState().video_player;

    const { current_user_is_owner: currentUserIsOwner, id: videoId } = video;
    const { full_screen_enabled: fullScreenEnabled } = videoAnalytics;

    // do not track video views for the owner - these will be discarded at the
    // API level anyhow
    if (currentUserIsOwner) {
      return;
    }

    const { anonID, deviceID } = analytics.getAnalyticsIds();

    fetch(`/v1/videos/${videoId}/view`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'POST',
      body: JSON.stringify({
        segmentAnonId: anonID,
        deviceID,
        fromLinkExpand,
        fullScreenEnabled,
        parentLocation,
        password,
        appSource: getAppSource(),
      }),
    })
      .then(resp => {
        if (resp.status >= 400) {
          throw new Error(
            `Oops! Error tracking video view - HTTP response: ${resp.status}`
          );
        }
      })
      .catch(err => {
        sentry.logException(err, 'error in track video view creator');
      });
  };
}

export function trackVideoWatchDurationCreator(duration) {
  return (dispatch, getState) => {
    const { video_player, workspace } = getState();

    const {
      analytics: videoAnalytics,
      duration: videoDuration,
      from_link_expand: fromLinkExpand,
      from_public_share_page: fromPublicSharePage,
      is_inline_embed_on_loom: isInlineEmbedOnLoom,
      isMainVideoOnSharePage,
      parent_location: parentLocation,
      video,
    } = video_player;

    const { selectedWorkspace } = workspace;

    // protect against overflow errors - we treat someone watching this video over
    // 1000 times in a single session as an anomaly that gets discarded
    if (duration > videoDuration * 1000) {
      return sentry.logException(
        new Error('large video watch track duration'),
        '',
        { duration, video_id: video.id }
      );
    }

    const {
      full_screen_enabled: fullScreenEnabled,
      playback_rate: speed,
      theater_mode_enabled: theaterModeEnabled,
    } = videoAnalytics;

    const { anonID, deviceID } = analytics.getAnalyticsIds();

    // we must use XHR here since fetch doesn't support synchronous requests and
    // this creator is called on window unload:
    //
    // https://github.com/github/fetch/issues/248
    new Promise((resolve, reject) => {
      const url = '/api/a/tvv';
      const xhr = new XMLHttpRequest();
      const reqBody = JSON.stringify({
        anonID,
        deviceID,
        duration,
        speed,
        embedded_on: isMainVideoOnSharePage ? null : document.referrer,
        from_link_expand: fromLinkExpand,
        from_public_share_page: fromPublicSharePage,
        from_preview_panel: fromPreviewPanel,
        is_inline_embed_on_loom: isInlineEmbedOnLoom,
        full_screen_enabled: fullScreenEnabled,
        parent_location: parentLocation,
        theater_mode_enabled: theaterModeEnabled,
        shared_app_source: sharedAppSource,
        organization_id: selectedWorkspace?.id,
        appSource: getAppSource(),
      });

      if (
        navigator.sendBeacon &&
        navigator.sendBeacon(url + '?beacon', reqBody)
      ) {
        return resolve();
      }

      // the third boolean param means 'synchronous'
      xhr.open('POST', url + '?xhr', true);

      xhr.onload = () => {
        if (xhr.readyState === 4) {
          if (xhr.status >= 400) {
            return reject(
              new Error(
                'Oops! Error tracking video watch duration - ' +
                  `HTTP response: ${xhr.status}`
              )
            );
          }

          resolve();
        }
      };

      xhr.onerror = () => {
        reject(
          new Error(
            'Oops! Error tracking video watch duration - ' +
              `HTTP response: ${xhr.status}`
          )
        );
      };

      xhr.send(reqBody);
    }).catch(err => {
      sentry.logException(err, 'error in track video watch duration creator');
    });
  };
}

export function updateVideoCreator(video) {
  return (dispatch, getState) => {
    // seed the video information before metadata has loaded
    const { height, width } = video.video_properties;
    const duration = videoUtils.getDuration(video);
    const {
      complete,
      flipped_camera: flippedCamera,
      needs_password: needsPassword,
      viewer_needs_permission: needsPermission,
      active_status: activeStatus,
    } = video;

    if (duration != null) {
      dispatch(updateVideoPlayerDurationCreator(duration));
    }

    if (height != null || width != null) {
      dispatch(updateVideoPlayerDimensionsCreator(width, height));
    }

    dispatch(updateVideoPlayerVideo(video));
    dispatch(
      updateTrimRangesCreator(video?.processing_information?.trim_ranges || [])
    );

    dispatch(updateVideoPlayerFlippedCamera(flippedCamera));
    dispatch(updateVideoPlayerNeedsPassword(needsPassword));

    if (needsPermission != null) {
      dispatch(updateVideoViewerNeedsPermission(needsPermission));
    }

    if (
      needsPermission ||
      needsPassword ||
      complete === false ||
      !activeStatus
    ) {
      return;
    }

    // we are waiting on a trim to finish processing - poll for the video source
    // after
    if (waitingForTrim(video, getState())) {
      return;
    }

    if (video.reactions) {
      dispatch(updateVideoPlayerReactions(video.reactions));
    }

    dispatch(updateVideoPlayerSourceCreator());
    dispatch(updateVideoCta(video.cta));
  };
}

export function updateVideoPlayerSourceCreator({
  forceOriginal = false,
  forceMp4 = false,
} = {}) {
  return async (dispatch, getState) => {
    try {
      const { password, video } = getState().video_player;

      dispatch(fetchVideoPlayerVideo());

      const posterUrl = videoUtils.getPosterUrl(video);

      const { partCredentials, sourceMimeType, sourceUrl } = await new Promise(
        resolve => {
          let getUrlHandler;
          let sourceMimeType;
          let supportedMimeTypes;

          // if we're forcing the original video, load the
          // raw url
          // Get raw URL can also return MP4
          if (forceOriginal || !forceMp4) {
            supportedMimeTypes = videoUtils.getSupportedMimeTypes();

            getUrlHandler = videoUtils.postSessionRawURL;
          } else {
            getUrlHandler = videoUtils.postSessionTranscodedURL;
            sourceMimeType = MP4;
          }

          getUrlHandler(
            video.id,
            password,
            (sourceUrl, partCredentials = {}) => {
              const a = document.createElement('a');

              a.href = sourceUrl;

              if (a.pathname.endsWith('.mp4')) {
                sourceMimeType = MP4;
              } else if (a.pathname.endsWith('.webm')) {
                sourceMimeType = WEBM;
              } else if (a.pathname.endsWith('.m3u8')) {
                sourceMimeType = M3U8;
              } else if (a.pathname.endsWith('.mpd')) {
                sourceMimeType = DASH;
              }

              resolve({ partCredentials, sourceMimeType, sourceUrl });
            },
            forceOriginal,
            supportedMimeTypes
          );
        }
      );

      // Dispatch the "plain" url. The one that will actually be used
      // by the player will be encoded in base64, in "sourceUrl"
      // We need the original one for mux report
      dispatch(updateVideoPlayerPlainSourceUrl(sourceUrl));

      const finalSourceUrl = await (() => {
        if (Object.keys(partCredentials).length === 0) {
          return sourceUrl;
        }

        if (sourceMimeType === M3U8) {
          // take the m3u8 playlist url, download it, and then use the part credentials
          // to create a blob URL of a modified playlist with the credentials attached
          return getHlsPlaylistWithCredentials(sourceUrl, partCredentials);
        } else if (sourceMimeType === DASH) {
          return getDashPlaylistWithCredentials(sourceUrl, partCredentials);
        }

        return sourceUrl;
      })();

      if (posterUrl) {
        dispatch(updateVideoPlayerPosterUrl(posterUrl));
      }

      const sourceUrlIsTranscoded = sourceUrl.includes('/sessions/transcoded/');

      dispatch(
        updateVideoPlayerSource({
          sourceUrl: finalSourceUrl,
          sourceMimeType,
          partCredentials,
          sourceUrlIsTranscoded,
        })
      );
    } catch (err) {
      sentry.logException(err, 'Error getting video sources');

      window.location.href = SERVER_ERROR_PAGE;
    }
  };
}

export function updateVideoPlayerDimensionsCreator(width, height) {
  return (dispatch, getState) => {
    const { isMainVideoOnSharePage } = getState().video_player;

    if (isMainVideoOnSharePage && height != null && width != null) {
      window.top.postMessage(
        {
          type: EMBED_DIMENSIONS_UPDATE,
          height,
          width,
        },
        '*'
      );
    }

    dispatch(updateVideoPlayerDimensions(width, height));
  };
}

export function updateVideoPlayerDurationCreator(duration) {
  return (dispatch, getState) => {
    const { isMainVideoOnSharePage } = getState().video_player;

    if (isMainVideoOnSharePage) {
      window.top.postMessage(
        {
          type: EMBED_VIDEO_DURATION,
          duration,
        },
        `${LOOM_URI}/share`
      );
    }

    dispatch(updateVideoPlayerDuration(duration));
  };
}

export function updateVideoPlayerPasswordCreator(password) {
  return (dispatch, getState) => {
    const {
      video_player: { isMainVideoOnSharePage, video },
    } = getState();

    // send a message to the public share page so it can render the comments
    // and description view
    if (isMainVideoOnSharePage) {
      window.top.postMessage(
        {
          type: EMBED_VIDEO_CORRECT_PASS,
          password,
        },
        `${LOOM_URI}/share`
      );
    }

    dispatch(updateVideoPlayerPassword(password));
    dispatch(updateVideoPlayerReactions(video.reactions));
    dispatch(updateVideoCta(video.cta));
    dispatch(fetchPasswordProtectedProps());
  };
}

function fetchPasswordProtectedProps() {
  return async (dispatch, getState) => {
    const { id: userId } = getState().user.current;
    const userIdForCheck = userId || getAnonUserId();

    // Input: reactions as we receive them from GraphQL
    // output: reactions as the player expects them
    function convertVideoReactions(reaction) {
      return {
        id: reaction.id,
        time: reaction.time,
        type: EMOJI_REACTIONS_MAP[reaction.reaction],
        username: reaction.user
          ? reaction.user.display_name
          : reaction.anon_user_name,
        can_delete:
          (reaction.user_id || reaction.anon_user_id) == userIdForCheck,
      };
    }

    try {
      const { password } = getState().video_player;
      const video = selectVideo(getState());
      const isMainVideoOnSharePage = selectIsMainVideoOnSharePage(getState());

      const client = getGraphQLClient();
      const { data } = await client.query({
        query: GetPasswordProtectedProps,
        variables: {
          password,
          videoId: video.id,
        },
        fetchPolicy: 'no-cache',
      });

      const { owner, description, name, video_reactions } = data?.video;

      if (isMainVideoOnSharePage) {
        dispatch(updateVideoDescriptionState(description));
        dispatch(updateShareVideoName(name));
      } else {
        dispatch(updateVideoPlayerName(name));
      }

      const { display_name, avatars } = owner;

      dispatch(updateVideoPlayerOwner(display_name, avatars[0]));

      const reactions = video_reactions.map(convertVideoReactions);

      dispatch(updateVideoPlayerReactions(reactions));
    } catch (err) {
      sentry.logException(err, 'error fetching video password protected props');
    }
  };
}

export function updateVideoPlayerSourceRawCreator() {
  return (dispatch, getState) => {
    const { password, video } = getState().video_player;
    const { id } = video;

    dispatch(updateVideoPlayerWaiting());

    return new Promise(resolve => {
      videoUtils.postSessionRawURL(id, password, sourceUrl => {
        const a = document.createElement('a');

        a.href = sourceUrl;

        const sourceUrlIsTranscoded = sourceUrl.includes(
          '/sessions/transcoded/'
        );

        if (a.pathname.endsWith('.mp4')) {
          dispatch(
            updateVideoPlayerSource({
              sourceUrl,
              sourceMimeType: MP4,
              sourceUrlIsTranscoded,
            })
          );
        } else if (a.pathname.endsWith('.webm')) {
          dispatch(
            updateVideoPlayerSource({
              sourceUrl,
              sourceMimeType: WEBM,
              sourceUrlIsTranscoded,
            })
          );
        } else if (a.pathname.endsWith('.m3u8')) {
          dispatch(
            updateVideoPlayerSource({
              sourceUrl,
              sourceMimeType: M3U8,
              sourceUrlIsTranscoded,
            })
          );
        } else if (a.pathname.endsWith('.mpd')) {
          dispatch(
            updateVideoPlayerSource({
              sourceUrl,
              sourceMimeType: DASH,
              sourceUrlIsTranscoded,
            })
          );
        }

        dispatch(updateVideoPlayerNotWaiting());
        resolve();
      });
    });
  };
}

export function updateVideoPlayerSourceMp4Creator() {
  return (dispatch, getState) => {
    const { password, video } = getState().video_player;
    const { id } = video;

    dispatch(updateVideoPlayerWaiting());

    videoUtils.postSessionTranscodedURL(id, password, sourceUrl => {
      const sourceUrlIsTranscoded = sourceUrl.includes('/sessions/transcoded/');

      dispatch(
        updateVideoPlayerSource({
          sourceUrl,
          sourceMimeType: MP4,
          sourceUrlIsTranscoded,
        })
      );

      dispatch(updateVideoPlayerNotWaiting());
    });
  };
}

export function createReaction({
  type,
  time: timeInTrimmedVid,
  fromEndScreen = false,
}) {
  return async (dispatch, getState) => {
    try {
      const localId = new Date().getTime();
      const trimRanges =
        getState().video_player.video.processing_information.trim_ranges;
      const actualTime = convertTrimTimeToFullTime(
        timeInTrimmedVid,
        trimRanges
      );

      dispatch(
        updateVideoPlayerEmojiSelected(localId, type, {
          localId,
          type,
          time: actualTime,
        })
      );

      const ANIMATION_DURATION = 1000; // in ms

      // Save timeout promise to avoid updating from server response mid-animation
      const emojiFadeoutPromise = new Promise(resolve => {
        window.setTimeout(resolve, ANIMATION_DURATION);
      }).then(() => {
        dispatch(updateVideoPlayerEmojiSelectedReset());
      });

      const { id: videoId } = getState().video_player.video;
      const videoOwnerId = getState().video_player.video.owner_id;
      const reactorId = getState().user.current.id;
      const anonUserName =
        selectAnonymousUsername(getState()) || getAnonUserName();
      const password = getState().video_player.password;

      const { ended: videoEnded } = getState().video_player;

      analytics.track(analyticsEvents.EMOJI_REACTION_CREATED, {
        reactor_id: reactorId,
        video_owner_id: videoOwnerId,
        video_id: videoId,
        sentiment: type,
        sentiment_emoji: EMOJIS[type],
        anonymous: !reactorId && !anonUserName,
        from_end_screen: fromEndScreen,
        video_ended: videoEnded,
      });

      const resp = await fetch('/v1/video-reactions', {
        credentials: 'include',
        headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
        method: 'POST',
        body: JSON.stringify({
          videoId,
          type,
          time: actualTime,
          user_name: anonUserName,
          password,
        }),
      });

      const reaction = await resp.json();

      // Wait for animation to finish
      await emojiFadeoutPromise;

      dispatch(updateVideoPlayerEmojiSelectedSaved(reaction.id, localId));

      if (resp.status >= 400) {
        throw new Error(
          `Error reacting to video - HTTP response: ${resp.status}`
        );
      }
    } catch (err) {
      sentry.logException(err, 'error reacting to video');
    }
  };
}

export function updateUsername(user_name) {
  return async (dispatch, getState) => {
    const truncatedUsername = (user_name || '').substring(
      0,
      MAX_USER_NAME_LENGTH
    );

    try {
      const resp = await fetch('/v1/auth/anon_username', {
        credentials: 'include',
        headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
        method: 'PATCH',
        body: JSON.stringify({ user_name: truncatedUsername }),
      });

      if (resp.status >= 400) {
        throw new Error(
          `Error reacting to video - HTTP response: ${resp.status}`
        );
      }

      const localReactions = getState().video_player.local_reactions.map(r => {
        return { ...r, username: user_name, showUsernameInput: false };
      });

      dispatch(updateVideoPlayerLocalReactions(localReactions));
      dispatch(updateAnonymousUsername(user_name));

      // TODO: Save username in app state and use it for comments as well
    } catch (err) {
      sentry.logException(err, 'error setting username for anonymous');
    }
  };
}

export function deleteReaction({ id: reactionId, type }) {
  return async (dispatch, getState) => {
    try {
      const { id: videoId } = getState().video_player.video;
      const videoOwnerId = getState().video_player.video.owner_id;
      const reactorId = getState().user.current.id;

      analytics.track(analyticsEvents.EMOJI_REACTION_DELETED, {
        reactor_id: reactorId,
        video_owner_id: videoOwnerId,
        video_id: videoId,
        sentiment: type,
        sentiment_emoji: EMOJIS[type],
      });

      dispatch(updateVideoPlayerRemoveReaction(reactionId));

      const resp = await fetch(`/v1/video-reactions/${reactionId}`, {
        credentials: 'include',
        headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
        method: 'DELETE',
      });

      if (resp.status >= 400) {
        throw new Error(
          `Error deleting reaction from video - HTTP response: ${resp.status}`
        );
      }
    } catch (err) {
      sentry.logException(err, 'error deleting video reaction');
    }
  };
}

export function activateReaction({ time, type }) {
  return (dispatch, getState) => {
    const { id: videoId } = getState().video_player.video;
    const videoOwnerId = getState().video_player.video.owner_id;
    const clickerId = getState().user.current.id;
    const hasStarted = getState().has_started;

    analytics.track(analyticsEvents.EMOJI_REACTION_CLICKED, {
      clicker_id: clickerId,
      video_owner_id: videoOwnerId,
      video_id: videoId,
      sentiment: type,
      sentiment_emoji: EMOJIS[type],
    });

    // When clicking emoji, jump to X seconds before reaction
    const SECONDS_BEFORE_REACTION = 3;

    dispatch(
      requestVideoPlayerCurrentTime(time / 1000 - SECONDS_BEFORE_REACTION)
    );

    // if emoji is clicked on pre-play screen, also play the video
    if (!hasStarted) {
      dispatch(requestVideoPlayerPlay());
    }
  };
}

function willPause(player) {
  return player.has_started && !player.paused;
}

export function videoPlayerUpdateAnonymousReactionMode(
  inAnonymousReactionMode
) {
  return (dispatch, getState) => {
    const player = getState()?.video_player || {};

    if (player.in_anonymous_reaction_mode === inAnonymousReactionMode) {
      return;
    }

    dispatch(updateVideoPlayerInAnonymousReactionMode(inAnonymousReactionMode));
  };
}

export function videoPlayerEnterCommentMode(fromEndScreen) {
  return (dispatch, getState) => {
    const player = getState()?.video_player || {};
    const { id: videoId } = player.video;

    if (player.in_comment_mode) {
      return;
    }

    const resumeOnExit = willPause(player);

    dispatch(requestVideoPlayerPause());

    dispatch(updateVideoPlayerInCommentMode(true, resumeOnExit));

    analytics.track(analyticsEvents.VIDEO_PLAYER_ENTER_COMMENT_MODE, {
      video_id: videoId,
      from_end_screen: fromEndScreen,
    });
  };
}

export function videoPlayerExitCommentMode() {
  return (dispatch, getState) => {
    const player = getState()?.video_player || {};

    // resume only if we paused on enter, and we're not commenting on the end screen
    if (player.resume_after_comment && !player.ended) {
      dispatch(requestVideoPlayerPlay());
    }

    dispatch(updateVideoPlayerInCommentMode(false));
  };
}

export function videoPlayerShowComments(commentId) {
  return (dispatch, getState) => {
    const player = getState()?.video_player || {};
    const { id: videoId } = player.video;

    analytics.track(analyticsEvents.VIDEO_PLAYER_SHOW_ALL_COMMENTS, {
      video_id: videoId,
    });

    const resumeOnExit = willPause(player);

    dispatch(requestVideoPlayerPause());

    dispatch(updateVideoPlayerShowComments(true, commentId, resumeOnExit));
  };
}

export function videoPlayerHideComments() {
  return (dispatch, getState) => {
    const player = getState()?.video_player || {};

    // resume only if we paused on enter
    if (player.resume_after_comment_overlay) {
      dispatch(requestVideoPlayerPlay());
    }

    dispatch(removeAllLocalDeleted());

    dispatch(updateVideoPlayerShowComments(false));
  };
}

export function watchAgain({
  targetClip = null,
  updateFocusedClipIndex = null,
  updateFocusedPlayRangeIndex = null,
} = {}) {
  return (dispatch, getState) => {
    const player = getState()?.video_player || {};
    const { id: videoId } = player.video;
    const { in_trim_mode: inTrimMode } = getState().trimming;

    analytics.track(analyticsEvents.WATCH_AGAIN_CLICK, { video_id: videoId });

    // if we are in trim/edit mode and watching a multi-clip video, we may need to go back to the very first clip
    if (inTrimMode && targetClip) {
      const playRanges = targetClip.playRanges;
      const startTime = playRanges?.[0].from ?? 0;

      dispatch(
        updateVideoPlayerSourceFromClip({
          sourceUrl: targetClip?.source_url,
          sourceMimeType: targetClip?.source_mime_type,
          partCredentials: targetClip?.source_part_credentials,
          sourceUrlIsTranscoded: targetClip?.source_url_is_transcoded,
          autoplay: true,
          seekTime: startTime,
        })
      );
      updateFocusedClipIndex(0);
      updateFocusedPlayRangeIndex(0);
    } else {
      dispatch(requestVideoPlayerCurrentTime(0));
      dispatch(requestVideoPlayerPlay());
    }
  };
}

export function skipBack(skipSeconds = PLAYER_LONG_STEP) {
  return (dispatch, getState) => {
    const state = getState();
    const player = state?.video_player || {};
    const { id: videoId } = player.video;
    const { in_trim_mode: inTrimMode, playRanges } = state?.trimming || {};

    const hasEnded = selectHasEnded(getState());
    const currentTime = selectPlayerTime(getState());

    const newTime = inTrimMode
      ? findNewTimeInPlayRanges(currentTime, -skipSeconds, playRanges)
      : currentTime - skipSeconds;

    dispatch(requestVideoPlayerCurrentTime(newTime));

    if (hasEnded) {
      dispatch(requestVideoPlayerPlay());
    }

    analytics.track(analyticsEvents.VIDEO_PLAYER_STEP, {
      stepType: STEP_BACK,
      stepDuration: skipSeconds,
      inTrimMode,
      videoId,
    });
  };
}

export function skipBackEditMode(skipSeconds = PLAYER_LONG_STEP, clipsState) {
  const {
    clips,
    focusedClipIndex,
    updateFocusedClipIndex,
    updateFocusedPlayRangeIndex,
  } = clipsState;

  return (dispatch, getState) => {
    const state = getState();
    const currentTime = selectPlayerTime(state);
    const { id: videoId } = state.video_player?.video || {};

    let currentClipIndex = focusedClipIndex;
    let currentClip = clips[focusedClipIndex];
    let newTime = findNewTimeInPlayRanges(
      currentTime,
      -skipSeconds,
      currentClip.playRanges,
      true
    );
    let remainingSecs = 0;

    if (newTime < 0) {
      remainingSecs = -newTime;

      while (remainingSecs > 0 && clips[currentClipIndex - 1] != null) {
        currentClipIndex--;
        currentClip = clips[currentClipIndex];

        const playRanges = currentClip.playRanges;

        newTime = findNewTimeInPlayRanges(
          playRanges[playRanges.length - 1].to,
          -remainingSecs,
          playRanges,
          true
        );

        remainingSecs = -newTime;

        console.log({
          currentClipIndex,
          currentClip,
          currentTime,
          newTime,
          skipSeconds,
          remainingSecs,
        });
      }
    }

    if (remainingSecs > 0) {
      newTime = 0;
    }

    dispatch(requestVideoPlayerCurrentTime(newTime));

    updateFocusedPlayRangeIndex(
      findCurrentPlayRangeIdx(newTime, currentClip.playRanges)
    );

    if (focusedClipIndex !== currentClipIndex) {
      const newClip = clips[currentClipIndex];

      replaceVideoPlayerClip(
        dispatch,
        updateFocusedClipIndex,
        newClip,
        currentClipIndex,
        {
          autoplay: true,
          requestPlay: currentClip?.source_url === newClip.source_url,
          seekTime: newTime,
        }
      );
    }

    analytics.track(analyticsEvents.VIDEO_PLAYER_STEP, {
      stepType: STEP_BACK,
      stepDuration: skipSeconds,
      inTrimMode: true,
      videoId,
    });
  };
}

export function skipForward(skipSeconds = PLAYER_LONG_STEP) {
  return (dispatch, getState) => {
    const state = getState();
    const player = state?.video_player || {};
    const { id: videoId } = player.video;
    const { in_trim_mode: inTrimMode, playRanges } = state?.trimming || {};

    if (inTrimMode && true) {
      dispatch(skipForwardEditMode(skipSeconds));

      return;
    }

    const currentTime = selectPlayerTime(getState());

    const newTime = inTrimMode
      ? findNewTimeInPlayRanges(currentTime, skipSeconds, playRanges)
      : currentTime + skipSeconds;

    if (newTime !== currentTime) {
      dispatch(requestVideoPlayerCurrentTime(newTime));

      analytics.track(analyticsEvents.VIDEO_PLAYER_STEP, {
        stepType: STEP_FORWARD,
        stepDuration: skipSeconds,
        inTrimMode,
        videoId,
      });
    }
  };
}

export function skipForwardEditMode(
  skipSeconds = PLAYER_LONG_STEP,
  clipsState
) {
  const {
    clips,
    focusedClipIndex,
    updateFocusedClipIndex,
    updateFocusedPlayRangeIndex,
  } = clipsState;

  return (dispatch, getState) => {
    const state = getState();
    const currentTime = selectPlayerTime(state);
    const { id: videoId } = state.video_player?.video || {};

    let currentClipIndex = focusedClipIndex;
    let currentClip = clips[focusedClipIndex];
    let newTime = findNewTimeInPlayRanges(
      currentTime,
      skipSeconds,
      currentClip.playRanges,
      true
    );
    let remainingSecs = 0;

    if (newTime > currentClip.duration) {
      remainingSecs = newTime - currentClip.duration;

      while (remainingSecs > 0 && clips[currentClipIndex + 1] != null) {
        currentClipIndex++;
        currentClip = clips[currentClipIndex];

        const playRanges = currentClip.playRanges;

        newTime = findNewTimeInPlayRanges(
          playRanges[0].from,
          remainingSecs,
          playRanges,
          true
        );

        remainingSecs = newTime - currentClip.duration;
      }
    }

    if (remainingSecs > 0) {
      newTime = currentClip.duration;
    }

    dispatch(requestVideoPlayerCurrentTime(newTime));

    if (focusedClipIndex !== currentClipIndex) {
      const newClip = clips[currentClipIndex];

      replaceVideoPlayerClip(
        dispatch,
        updateFocusedClipIndex,
        newClip,
        currentClipIndex,
        {
          autoplay: true,
          requestPlay: currentClip?.source_url === newClip.source_url,
          seekTime: newTime,
        }
      );
    }

    const newFocusedPlayRangeIndex = findCurrentPlayRangeIdx(
      newTime,
      currentClip.playRanges
    );

    updateFocusedPlayRangeIndex(newFocusedPlayRangeIndex);

    analytics.track(analyticsEvents.VIDEO_PLAYER_STEP, {
      stepType: STEP_FORWARD,
      stepDuration: skipSeconds,
      inTrimMode: true,
      videoId,
    });
  };
}

function replaceVideoPlayerClip(
  dispatch,
  updateFocusedClipIndex,
  newClip,
  newClipIndex,
  options = {}
) {
  updateFocusedClipIndex(newClipIndex);
  dispatch(
    updateVideoPlayerSourceFromClip({
      sourceUrl: newClip.source_url,
      sourceMimeType: newClip.source_mime_type,
      partCredentials: newClip.source_part_credentials,
      sourceUrlIsTranscoded: newClip.source_url_is_transcoded,
      ...options,
    })
  );
}

export function repositionPlayHeadInPlayRange({
  newPlayerTime,
  updateSelectedPlayRange,
} = {}) {
  return dispatch => {
    updateSelectedPlayRange();
    dispatch(requestVideoPlayerCurrentTime(newPlayerTime));
  };
}

export function skipToNextClip(
  updateFocusedClipIndex,
  clips,
  currentClipIndex,
  options = {}
) {
  return dispatch => {
    const nextClipIndex = currentClipIndex + 1;
    const nextClip = clips[nextClipIndex];
    const nextClipStartTime = nextClip.playRanges[0].from;

    replaceVideoPlayerClip(
      dispatch,
      updateFocusedClipIndex,
      nextClip,
      nextClipIndex,
      {
        ...options,
        seekTime: nextClipStartTime,
      }
    );
  };
}
